import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, MenuItem, TextField } from "@mui/material";
import moment from 'moment';
import Langs, { Localize } from "../../app/lang/langs";
import FormDialog, { FormButton, FormRow, formFieldSx } from "../../components/popups/FormDialog";
import FormDatePicker from "../../components/inputs/FormDatePicker";
import { getCollabsNames } from "../../components/vcomp/vcompSlice";
import { addCollabMission, ctGetCollabMissions } from "../../components/teams/teamsSlice";
import { getTeamCollabs } from "../../components/clients/clientsSlice";
import { showErrorToast } from "../../app/utils/helpers";
import { getPostes } from "../../components/collabs/collabsSlice";

const ISO = "YYYY-MM-DD";
const today = () => moment().format(ISO);

export default function AddTeamsMembersModal(props) {

    const dispatch = useDispatch();

    const collabsNames = useSelector((state) => state.vcomp.collabsNames);
    const postes = useSelector((state) => state.collabs.postes);
    const collabMissions = useSelector((state) => state.teams.collabMissions);

    const [associatedMembers, setAssociatedMembers] = useState(null)
    // `YYYY-MM-DD` strings, as FormDatePicker speaks them; "" is no date.
    const [startDate, setStartDate] = useState(today());
    const [endDate, setEndDate] = useState("");
    const [newPoste, setNewPoste] = useState("")
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (props.visible) {
            dispatch(getCollabsNames())
            dispatch(getPostes())
        }
    }, [props.visible, props.idTeam])

    useEffect(() => {
        if (associatedMembers && associatedMembers.id) {
            dispatch(ctGetCollabMissions({ id_collab: associatedMembers.id }))
        }
    }, [associatedMembers])

    const filteredCollabs = useMemo(() => (
        (collabsNames || [])
            .filter(collab => !(props.prevCollabs || []).includes(collab.id))
            .map(({ id, nom, prenom }) => ({ id, fullName: `${nom || ""} ${prenom || ""}`.trim() }))
    ), [collabsNames, props.prevCollabs])

    // The days already covered by one of the selected collab's missions: a new mission
    // cannot start or end on them. An open-ended mission blocks the next 30 years.
    const missionDays = useMemo(() => {
        const days = new Set();
        if (!associatedMembers || !collabMissions) return days;
        const allMissions = [
            ...(collabMissions.ongoingMissions || []),
            ...(collabMissions.upcomingMissions || []),
            ...(collabMissions.pastMissions || []),
        ];
        allMissions.forEach((mission) => {
            if (!mission.start_date) return;
            const current = moment(mission.start_date).startOf('day');
            const end = mission.end_date ? moment(mission.end_date) : moment().add(30, 'years');
            while (current.isSameOrBefore(end, 'day')) {
                days.add(current.format(ISO));
                current.add(1, 'day');
            }
        });
        return days;
    }, [associatedMembers, collabMissions]);

    const canSubmit = !!(associatedMembers && associatedMembers.id && props.idTeam && newPoste && startDate &&
        (!endDate || endDate >= startDate));

    const handleClose = () => {
        if (saving) return;
        props.setVisible(false);
        setAssociatedMembers(null)
        setStartDate(today());
        setEndDate("");
        setNewPoste("");
    }

    const handleAddMembers = () => {
        if (!canSubmit) return;

        setSaving(true);
        dispatch(addCollabMission({
            id_team: props.idTeam,
            id_collab: associatedMembers.id,
            start_date: `${startDate} 00:00:00`,
            end_date: endDate ? `${endDate} 23:59:59` : null,
            id_poste: newPoste
        }))
            .then((actionResponse) => {
                setSaving(false);
                if (addCollabMission.fulfilled.match(actionResponse)) {
                    const responseData = actionResponse.payload.responseData;
                    if (responseData && responseData.status) {
                        dispatch(getTeamCollabs({ id_team: props.idTeam }));
                        props.setVisible(false);
                        setAssociatedMembers(null)
                        setStartDate(today());
                        setEndDate("");
                        setNewPoste("");
                    } else if (responseData && responseData.error && responseData.error.includes("conflict")) {
                        showErrorToast(<Langs str="MISSION_CONFLICT_ERROR" />)
                    }
                }
            })
            .catch((error) => {
                setSaving(false);
                console.error(error)
                console.error('Échec de l\'ajout/suppression des membres');
            })
    }

    const disableMissionDay = (day) => missionDays.has(day);

    return (
        <FormDialog
            open={props.visible}
            onClose={handleClose}
            title={<Langs str="ADD_MEMBERS" />}
            actions={<>
                <FormButton variant="primary" onClick={handleAddMembers} loading={saving} disabled={!canSubmit}>
                    <Langs str="ADD" />
                </FormButton>
                <FormButton variant="secondary" onClick={handleClose} disabled={saving}>
                    <Langs str="CANCEL" />
                </FormButton>
            </>}
        >
            <FormRow label={<Langs str="Associated Members" />} required>
                <Autocomplete
                    value={associatedMembers}
                    onChange={(e, value) => setAssociatedMembers(value)}
                    options={filteredCollabs}
                    getOptionLabel={(option) => option.fullName}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    noOptionsText={Localize("No results")}
                    renderOption={(optionProps, option) => (
                        <li {...optionProps} key={option.id}>{option.fullName}</li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder={Localize("SELECT_COLLAB_PLACEHOLDER")}
                            sx={formFieldSx}
                        />
                    )}
                />
            </FormRow>
            <FormRow label={<Langs str="POSTE" />} required>
                <TextField
                    select
                    fullWidth
                    size="small"
                    value={newPoste}
                    onChange={(e) => setNewPoste(e.target.value)}
                    sx={formFieldSx}
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (id) => {
                            const poste = (postes || []).find(p => p.id === id);
                            return poste
                                ? poste.name
                                : <span style={{ color: "rgba(0, 40, 128, 0.4)" }}>{Localize("SELECT_POSTE")}</span>;
                        },
                    }}
                >
                    {(postes || []).map(poste => (
                        <MenuItem key={poste.id} value={poste.id}>{poste.name}</MenuItem>
                    ))}
                </TextField>
            </FormRow>
            <FormRow label={<Langs str="START_DATE" />} required>
                <FormDatePicker
                    value={startDate}
                    onChange={setStartDate}
                    shouldDisableDate={disableMissionDay}
                />
            </FormRow>
            <FormRow label={<Langs str="END_DATE" />}>
                <FormDatePicker
                    value={endDate}
                    onChange={setEndDate}
                    minDate={startDate || undefined}
                    shouldDisableDate={disableMissionDay}
                    clearable
                />
            </FormRow>
        </FormDialog>
    )
}
